import { Autocomplete, ListItem, ListItemText, TextField, Typography } from "@mui/material";
import { useMemo } from "react";
import { combineComparers, priorityComparer, stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import type { Category } from "../../../../api/types/accessTypes";
import { categoryTypeCaptionsMap } from "../../../../utilities/enumCaptions";
import { useUserConsentsSectionContext } from "./UserConsentsSectionContext";

interface Props {
  categoryId: string | undefined;
  onChange: (newCategoryId: string | undefined) => void;
}

interface CategoryOption {
  id: string;
  name: string;
  type: string;
  group: string;
}

const createCategoryOption = (category: Category): CategoryOption => ({
  id: category.id,
  name: category.name,
  type: category.type,
  group: categoryTypeCaptionsMap.get(category.type) ?? autoFormatCamelCase(category.type),
});

function UserConsentCategorySelect({ onChange, categoryId }: Props) {
  const { categories } = useUserConsentsSectionContext();

  const categoryOptions: CategoryOption[] = useMemo(() => {
    const sortComparer = combineComparers<CategoryOption>(
      priorityComparer((c) => c.type === "Message"),
      stringComparerBy((c) => c.group),
      stringComparerBy((c) => c.name)
    );

    return [
      { id: "all", name: "All categories", type: "", group: "" },
      ...categories.map(createCategoryOption).sort(sortComparer),
    ];
  }, [categories]);

  const handleChange = (_e: React.SyntheticEvent, newValue: CategoryOption | null) => {
    onChange(newValue?.id && newValue.id !== "all" ? newValue.id : undefined);
  };

  const selectedOption =
    categoryId === undefined
      ? categoryOptions[0]
      : (categoryOptions.find((option) => option.id === categoryId) ?? null);

  return (
    <Autocomplete<CategoryOption>
      fullWidth
      options={categoryOptions}
      value={selectedOption}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, item) => option && item && option.id === item.id}
      renderInput={(params) => <TextField {...params} label="Category" />}
      onChange={handleChange}
      groupBy={(option) => option.group}
      renderOption={({ key: _key, ...props }, option) => (
        <ListItem key={option.id} {...props} dense>
          <ListItemText
            primary={<Typography variant={option.id === "all" ? "subtitle2" : "body1"}>{option.name}</Typography>}
          />
        </ListItem>
      )}
    />
  );
}

export default UserConsentCategorySelect;
